// Hooks
import useEventTracking from '../../../hooks/useEventTracking';
import useRewindDuration from '../../../hooks/useRewindDuration';

// Helpers
import f2 from '../../../helpers/formatter2';
import { excludeIdFromUrl } from '../../../helpers/reminder';
import { safePlayerPlay } from '../../../helpers/playerGuards';

// Components
import AddReminderButton from '../../../components/elements/AddReminderButton';
import AddWatchlistButton from '../../../components/elements/AddWatchlistButton';
import Loader from '../../../components/Loader';

// Translations
import { __ } from '../../../translations';
import { useEpgGuide, useEpgLocalization, useLiveProgram } from '../hooks';
import styles from './styles/tvguide.module.sass';
import { useMediaQuery } from '@intigral/hooks';
import useAvod from '../../../hooks/useAvod';
import { FlexWrapper, FontIcon, Skeleton, Text } from '@intigral/shared';
import { ChannelsSearch, EPG } from '@intigral/ui';
import { AdBreakEvent, AdEvent, LinearAd, PlayerAPI, PlayerEvent } from 'bitmovin-player';
import cn from 'classnames';
import { createContext, RefObject, useEffect, useState } from 'react';
import { ProgramPoster, EpgPlayer, ProgramDetails } from './elements';
import { getChannelTypes, getSearchedChannels, handleChannelSelect } from '../utils/ChannelUtils';
import { EpgGuideContainerProps } from '../types/iEpgGudeContainer';
import { userAccessControl } from '../../../utils/userAccessControl';

export const EpgContainerRefContext = createContext<RefObject<HTMLDivElement | null> | null>(null);

const EpgGuideContainer: React.FC<EpgGuideContainerProps> = ({ searchParameters, userChannels }) => {
    // hooks
    const {
        streamingUrl,
        isStartOver,
        isFutureProgram,
        programs,
        channels,
        genres,
        selectedCategory,
        selectedDate,
        isFavorite,
        search,
        selectedProgram,
        isRewindProgram,
        favorites,
        epgDates,
        searchChannels,
        language,
        reminderData,
        isShowEpgLock,
        currentProgramRemainingTime,
        currentProgramDuration,
        isSwitch,
        setSearchChannels,
        onPlayerReadyCallback,
        onCategoryChangeHandler,
        onDateChangeHandler,
        onFavoriteClickHandler,
        handlePPVPurchase,
        onGoLiveHandler,
        onLockClickHandler,
        startOverPlayback,
        onProgramSelectHandler,
        onChannelSelectHandler,
        onChannelSearchSelectHandler,
        onRewindCompleted,
        mutation,
        sendBookmark,
        fetchPreviousProgramOnScroll,
        fetchFutureProgramOnScroll,
        getProgramMutation,
    } = useEpgGuide(searchParameters, userChannels);

    const { sendChannelSearchSelectEvent } = useEventTracking();
    const isMobile = useMediaQuery('(max-width: 924px)');
    const localization = useEpgLocalization();
    const isProgramLive = useLiveProgram();
    const [isClientSideAdPlaying, setIsClientSideAdPlaying] = useState(false);
    const { onAdBeginUI, updateAdsUI } = useAvod('', false, {});

    // Bitmovin exposes the advertising module once ads are initialized.
    //
    // `const ads = player.ads;
    // if (ads && ads.isPlaying()) {
    // }`
    //
    // Notes:
    // player.ads may be undefined before ads initialize
    // isPlaying() only works after the ad module is ready
    // Still recommend combining with events
    //
    // TODO: move into a hook
    const playerReadyCallback = (player: PlayerAPI) => {
        let ad;
        let adsCount;
        let currentAdPosition = 1;

        player.on(PlayerEvent.AdStarted, (event: AdEvent & { ad: LinearAd }) => {
            ad = event.ad;
            setIsClientSideAdPlaying(true);
            updateAdsUI(currentAdPosition, adsCount, event.ad.duration * 1000);
            currentAdPosition += 1;
        });

        const onAdEnd = () => {
            setIsClientSideAdPlaying(false);
            ad = null;
        };

        player.on(PlayerEvent.AdFinished, onAdEnd);
        player.on(PlayerEvent.AdSkipped, onAdEnd);
        player.on(PlayerEvent.AdError, onAdEnd);
        player.on(PlayerEvent.Error, onAdEnd);
        player.on(PlayerEvent.Destroy, onAdEnd);

        // TODO: should this be merged with handlePlayerTimeChanged() in usePlayerHandler.ts?
        player.on(PlayerEvent.TimeChanged, () => {
            if (!ad) return;
            onAdBeginUI(player, { ...ad, duration: ad.duration * 1000 });

            // TODO: listener for ad end that can be use to hide epg-overlay in case this was not handled by `onAdEnd`
        });

        const togglePlaybackOnVisibilityChange = function () {
            if (document.hidden) {
                // Tab is inactive - pause the player (content or ad)
                if (player.isPlaying()) {
                    player.pause();
                }
            } else {
                // Tab is active again - resume the player
                // Optionally check if it was playing before to avoid auto-playing
                // a user-paused video.
                safePlayerPlay(player);
            }
        };

        player.on(PlayerEvent.AdBreakStarted, (event: AdBreakEvent) => {
            adsCount = event.adBreak.ads.length;

            // TODO: move to BaseBitmovinPlayer
            document.addEventListener('visibilitychange', togglePlaybackOnVisibilityChange);
        });

        player.on(PlayerEvent.AdBreakFinished, () => {
            document.removeEventListener('visibilitychange', togglePlaybackOnVisibilityChange);
        });

        onPlayerReadyCallback(player);
    };

    useEffect(() => {
        /**
         * Manually scrolling to top of the page on page load
         *
         * In some browsers, e.g. Firefox, Live TV page is scrolling by itself to bottom of the page in certain
         * situations. For example when coming to live tv page after selecting live tv channel in streaming carousel
         * on home page
         *
         * @todo this might be not the best solution and the cause of this behavior should be identified and fixed
         */
        document.querySelector('html')?.scrollTo({ top: 0 });
    }, []);

    if (mutation.isPending || mutation.isIdle)
        return (
            <div style={{ height: '100vh' }}>
                <Skeleton />
            </div>
        );

    const isLive = isProgramLive && !isFutureProgram && !isRewindProgram && !isClientSideAdPlaying;

    const channelsSearch = (
        <ChannelsSearch
            goLiveAction={onGoLiveHandler}
            isLive={isLive}
            liveButtonLabel={isLive ? localization?.live ?? 'Live' : localization?.goLive ?? 'Go live'}
            channelTypes={getChannelTypes(genres)}
            channels={getSearchedChannels(
                searchChannels.filter((channel) => userAccessControl.canAccessChannel(channel))
            )}
            noResultsMsg={localization?.noResultsMsg || ''}
            searchLabel={localization?.searchLabel || ''}
            selectedType={selectedCategory ?? 'All'}
            typeSelect={onCategoryChangeHandler ?? (() => null)}
            textChange={(query) => setSearchChannels(query.length ? search.find(query) : channels)}
            channelSelect={(selected, query) =>
                handleChannelSelect(selected, query, onChannelSearchSelectHandler, sendChannelSearchSelectEvent)
            }
            onClickLock={onLockClickHandler}
            days={f2.getReadableFormattedDates(epgDates) ?? []}
            selectedDay={selectedDate ?? ''}
            daySelect={onDateChangeHandler ?? (() => null)}
            localization={localization}
            isClientSideAdPlaying={isClientSideAdPlaying}
        />
    );

    const player = (
        <div className={styles.player}>
            <ProgramPoster
                isFutureProgram={isFutureProgram}
                selectedProgram={selectedProgram}
                isShowEpgLock={isShowEpgLock}
                isReminderSet={false}
                reminderData={reminderData}
            />
            <EpgPlayer
                streamingUrl={streamingUrl}
                isStartOver={isStartOver}
                isFutureProgram={isFutureProgram}
                isRewindProgram={isRewindProgram}
                isSwitch={isSwitch}
                selectedProgram={selectedProgram}
                startOverPlayback={startOverPlayback}
                onRewindCompleted={onRewindCompleted}
                playerReadyCallback={playerReadyCallback}
                sendBookmark={sendBookmark}
                currentProgramRemainingTime={isClientSideAdPlaying ? 0 : currentProgramRemainingTime}
                currentProgramDuration={currentProgramDuration}
                currentProgramDurationTimeType={__('min')}
            />
        </div>
    );

    return (
        <div className={cn(styles.holder, { [styles.disabled]: isClientSideAdPlaying })}>
            {isClientSideAdPlaying && <div className={cn(styles.holder, styles['epg-overlay'])} />}
            {!isMobile && channelsSearch}
            {(!programs.length || !channels.length) && (
                <FlexWrapper
                    style={{
                        inset: '0',
                        top: '120px',
                        backgroundColor: 'var(--primary-colors-background)',
                        zIndex: 10,
                    }}
                    padding="10x"
                    justifyContent="start"
                    gap="2x"
                    flexDirection="column"
                >
                    <FontIcon name="info" size="size60" md="size60" />
                    <Text as="h2" variant="h2" margin="0">
                        {localization?.noResultsMsg}
                    </Text>
                    <Text as="p" variant="text3">
                        {localization?.noResultsMsg}
                    </Text>
                </FlexWrapper>
            )}
            <div className={cn('container full-width', styles.tvguide)}>
                {isMobile && (
                    <>
                        {channels.length ? player : null}
                        <div style={{ paddingTop: channels.length ? '0' : '40px' }}>{channelsSearch}</div>
                    </>
                )}

                {channels.length && programs.length ? (
                    <>
                        <EPG
                            date={selectedDate ?? 'no-date'}
                            onProgramSelect={onProgramSelectHandler}
                            onChannelSelect={onChannelSelectHandler}
                            channels={channels}
                            programs={programs}
                            isProgramCellsWidthFixed={true}
                            isStorybook={false}
                            selectedProgram={selectedProgram}
                            isRTL={language === 'ar'}
                            favoriteIDs={favorites?.map((fav: any) => fav?.id)}
                            useRewindDuration={useRewindDuration}
                            lang={language}
                            liveTag={localization?.live}
                            beforeTag={localization?.before}
                            afterTag={localization?.after}
                            onFavoriteClick={onFavoriteClickHandler}
                            epgLocalization={localization}
                            AddWatchListButton={AddWatchlistButton}
                            AddReminderButton={AddReminderButton}
                            reminderData={reminderData}
                            excludeIdFromUrl={excludeIdFromUrl}
                            fetchPreviousProgramOnScroll={fetchPreviousProgramOnScroll}
                            fetchFutureProgramOnScroll={fetchFutureProgramOnScroll}
                        />

                        <div className={styles.information}>
                            {!isMobile && player}
                            <ProgramDetails
                                selectedProgram={selectedProgram}
                                lang={language}
                                AddReminderButton={AddReminderButton}
                                AddWatchListButton={AddWatchlistButton}
                                isFutureProgram={isFutureProgram}
                                isFavorite={isFavorite}
                                onFavoriteClick={onFavoriteClickHandler}
                                onPPVPurchaseClick={handlePPVPurchase}
                            />
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default EpgGuideContainer;
